import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { ApiErrorResponse } from 'services/types'

import { getRequestActivity } from './getRequestActivity'
import { mapRequestActivityEvent } from './mapRequestActivityEvent'
import { REQUEST_ACTIVITY_DEFAULT_PAGE, REQUEST_ACTIVITY_MAX_PER_PAGE } from './paths'
import { RequestActivityQueryKeys } from './queryKeys'
import { sortReviewActivityEventsNewestFirst } from './sortReviewActivityEventsNewestFirst'
import type { RequestActivityEvent, RequestActivitySource, ReviewActivityListResponse } from './types'

export interface UseRequestActivityResult {
  events: RequestActivityEvent[]
  isLoading: boolean
  isError: boolean
}

export const useRequestActivity = (source: RequestActivitySource): UseRequestActivityResult => {
  const { requestType, requestId, rejectionReason, importSource, importCounts } = source

  const { data, isLoading, isError } = useQuery<ReviewActivityListResponse, ApiErrorResponse>({
    queryKey: RequestActivityQueryKeys.ForRequest(requestType, requestId),
    queryFn: ({ signal }) =>
      getRequestActivity(
        { requestType, requestId, page: REQUEST_ACTIVITY_DEFAULT_PAGE, perPage: REQUEST_ACTIVITY_MAX_PER_PAGE },
        { signal }
      ),
    enabled: Boolean(requestId)
  })

  const events = useMemo(
    () =>
      sortReviewActivityEventsNewestFirst(data?.data ?? []).map(event =>
        mapRequestActivityEvent(event, { requestType, requestId, rejectionReason, importSource, importCounts })
      ),
    [data, importCounts, importSource, rejectionReason, requestId, requestType]
  )

  return { events, isLoading, isError }
}
